import { Col, Input, PostsGrid, Row, Spinner2 } from 'components';
import { getCalculatedText, useInfiniteScroll } from 'core';
import { Api, theme } from 'lib';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { Filters, SearchOverlay, Suggestions } from './components';

const DirectionChange = styled.div`
  display: flex;
  flex-direction: ${(props) => (props.isMobile ? 'column' : 'row')};
  margin: ${(props) => (props.isMobile ? '16px 0 0 0' : '40px 0 0')};
  justify-content: flex-start;
  align-items: ${(props) => (props.isMobile ? 'start' : 'center')};
`;

const Browse = (props) => {
  const [type, setType] = useState(''),
    [loading, setLoading] = useState('initial'),
    [sports, setSports] = useState([]),
    [text, setText] = useState(''),
    [listComplete, setListComplete] = useState(false),
    [searching, setSearching] = useState(false),
    [latestPostDate, setLatestPostDate] = useState(null),
    browse = useSelector((state) => state.browse),
    { posts, users } = browse || {},
    { isMobile } = useSelector((state) => state.appState),
    dispatch = useDispatch(),
    inputRef = useRef(),
    { t } = useTranslation();

  useInfiniteScroll(null, getData, { type });

  useEffect(() => {
    if (!browse) {
      dispatch({ type: 'SET_BROWSE' });
    }

    getData(true);
  }, [type, sports]);

  function handleChange(val, type) {
    setText(val);
  }

  async function getData(initial) {
    if (!initial && isMobile) return;
    if (!initial && loading) return;
    setLoading(initial ? 'initial' : true);

    const res = await Api.post('/browse/getData', {
      type,
      fetchStartDate: initial ? new Date() : latestPostDate,
      sports: sports?.filter(Boolean)
    });

    setLoading(false);
    if (!res) return;

    // Add read more previews to the captions
    const browsePosts = await Promise.all(
      res.posts.map(async (x) => ({
        ...x,
        calculatedText: await getCalculatedText(x.caption)
      }))
    );

    res.posts = browsePosts;

    if (initial) {
      dispatch({
        type: 'SET_BROWSE',
        payload: res
      });

      dispatch({
        type: 'ADD_POSTS',
        payload: res.posts
      });
    } else {
      dispatch({
        type: 'ADD_POSTS',
        payload: res.posts
      });

      dispatch({
        type: 'ADD_POSTS_BROWSE',
        payload: res.posts
      });
    }

    setLatestPostDate(res.latestDate);
    setLoading(false);
  }

  return (
    <Col wid="100%" centerAll pad="0 0 119px 0" ht="calc(100vh - 193px)" start>
      <Row noFlex pad="0 0 84px" wid={isMobile ? '100vw' : '936px'} start>
        <Col wid={isMobile ? '100vw' : 'auto'}>
          <DirectionChange isMobile={isMobile}>
            <Row
              marg={isMobile ? '0 0 16px 0' : '0 24px 0 0'}
              wid={isMobile && '100%'}
              centerAll={isMobile}
            >
              <Input
                wid={isMobile ? 'calc(100% - 32px)' : '296px'}
                search
                value={text}
                onChange={(e) => handleChange(e.target.value)}
                hasBack={
                  searching &&
                  (() => {
                    setText('');
                    setSearching(false);
                    inputRef.current.blur();
                  })
                }
                hasRemove
                placeholder={t('common.search')}
                inputRef={inputRef}
                onFocus={() => setSearching(true)}
                onBlur={() => setTimeout(() => setSearching(false), 300)}
              />

              <SearchOverlay value={text} visible={searching} setVisible={setSearching} />
            </Row>

            <Filters sports={sports} setSports={setSports} type={type} setType={setType} />
          </DirectionChange>

          {loading === 'initial' ? (
            <Col wid="100%" marg="32px 0 0" noFlex centerAll>
              <Spinner2 col={theme.SECONDARY} />
            </Col>
          ) : (
            <Col marg={isMobile ? '16px 0 0' : '32px 0 0'} gap={isMobile ? '16px' : '40px'}>
              {!!users?.athlete?.length && (
                <Suggestions
                  title={t('browse.athletesOnPlatform')}
                  allUsersType="athlete"
                  data={users.athlete}
                  subtitleText={(user) =>
                    user.sports?.length && user.sports[0]?.sport
                      ? t(`sports.${user.sports[0].sport}`)
                      : ''
                  }
                />
              )}

              {!!users?.fan?.length && (
                <Suggestions
                  title={t('browse.fansOnPlatform')}
                  allUsersType="fan"
                  data={users.fan}
                  subtitleText={(user) =>
                    `${user.followersCount} follower${user.followersCount !== 1 ? 's' : ''}`
                  }
                />
              )}

              <Suggestions
                title={t('browse.teamsOnPlatform')}
                data={users.team}
                allUsersType="team"
                showFollowing
                subtitleText={(user) => {
                  const league = user.league ? user.league : '',
                    sport = user.sport ? t(`sports.${user.sport}`) : '';

                  return sport || league
                    ? `${league}${sport ? `${league ? ' ' + sport + ' club' : ''}` : ''}`
                    : '';
                }}
              />

              <PostsGrid title={t('browse.trendingPosts')} data={posts} fetchMore={getData} />

              {loading && !listComplete && (
                <Col marg="20px 0 0" centerAll wid="100%">
                  <Spinner2 col={theme.SECONDARY} />
                </Col>
              )}
            </Col>
          )}
        </Col>
      </Row>
    </Col>
  );
};

export default Browse;
